// src/components/HeroSection.tsx
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, Phone } from 'lucide-react';
import { useLocation } from 'react-router-dom';

interface HeroSectionProps {
  title: string;
  subtitle?: string;
  imageSrc: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

const HeroSection = ({
  title,
  subtitle,
  imageSrc,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
}: HeroSectionProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <div className={`relative h-[500px] md:h-[600px] w-full overflow-hidden ${isHomePage ? 'mt-16' : 'mt-0'}`}>
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center brightness-110 contrast-105" 
        style={{ backgroundImage: `url(${imageSrc})` }}
      >
        {/* Enhanced overlay for better contrast and visual appeal */}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/60 via-stone-900/50 to-stone-900/60"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 h-full container-custom flex flex-col justify-center items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl text-center mx-auto"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 text-shadow text-center">
            {title}
          </h1>
          
          {subtitle && (
            <p className="text-xl md:text-2xl text-white opacity-90 mb-8 text-shadow text-center">
              {subtitle}
            </p>
          )}
          
          <div className="flex flex-wrap gap-4 justify-center">
            {isHomePage ? (
              <>
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white group" asChild>
                  <a href="/kontakt" className="flex items-center gap-2">
                    Zahtevaj ponudbo <ChevronRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="bg-transparent text-white hover:bg-white/10 border-white hover:border-white group" asChild>
                  <a href="tel:031467272" className="flex items-center gap-2">
                    <Phone size={16} className="transition-transform duration-300 group-hover:translate-x-[-2px]" /> 031 467 272
                  </a>
                </Button>
              </>
            ) : (
              <>
                {primaryButtonText && (
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white group" asChild>
                    <a href={primaryButtonLink || '#'} className="flex items-center gap-2">
                      {primaryButtonText} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </Button>
                )}
                
                {secondaryButtonText && (
                  <Button size="lg" variant="outline" className="bg-transparent text-white hover:bg-white/10 border-white hover:border-white group" asChild>
                    <a href={secondaryButtonLink || '#'}>
                      {secondaryButtonText}
                    </a>
                  </Button>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HeroSection;